import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FormatDate } from '../../_objects/format-date';
import { Subject, SubjectClone, SubjectNew, SubjectUpdate } from '../../_objects/subject';
import { SubjectModel } from '../../_objects/subject-model';
import { HttpErrorMessage, HttpStatusText } from '../../_offline/electron.constants';
import { SubjectSchema } from '../../_offline/models/subject.schema';
import { GUID } from '../../app.constants';
import { SubjectDbService } from '../database/subject-db.service';
import { SubjectModelDbService } from '../database/subject-model-db.service';
import { SubjectContractService } from '../interfaces/subject-contract.service';
import { LoginUserOfflineService } from './login-user-offline.service';
import { SubjectAttributesSchema } from '../../_offline/models/subject-attributes.schema';
import { InspectionDbService } from '../database/inspection-db.service';

@Injectable({
  providedIn: 'root'
})
export class SubjectOfflineService implements SubjectContractService {

  constructor(
    private loginUserOfflineService: LoginUserOfflineService,
    private subjectDbService: SubjectDbService,
    private subjectModelDbService: SubjectModelDbService,
    private inspectionDbService: InspectionDbService,
  ) { }

  // SB01 GET /api/v1/subjects
  getSubjects(subjectModelId?: string, enabledOnly?: boolean): Observable<Subject[]> {
    return this.subjectDbService.getAll(subjectModelId, enabledOnly)
      .pipe(map(schemas => schemas.map(schema => this.createSubject(schema))));
  }

  // SB02 GET /api/v1/subjects/{subjectId}
  getSubject(id: string, version?: number): Observable<Subject> {
    return forkJoin({
      subject: this.subjectDbService.getOne(id),
      hasInspections: this.inspectionDbService.hasInspections(id),
    }).pipe(map((x) => this.createSubject(x.subject, version, x.hasInspections)));
  }

  // SB03 POST /api/v1/subjects
  addSubject(subject: SubjectNew): Observable<Subject> {
    const date: string = new FormatDate().toString();

    const subjectSchema: SubjectSchema = new SubjectSchema(this.loginUserOfflineService.tenantId);
    subjectSchema.schemaVersion = subject.schemaVersion;
    subjectSchema.createdBy = this.loginUserOfflineService.loginUser.id;
    subjectSchema.lastUpdatedBy = this.loginUserOfflineService.loginUser.id;
    subjectSchema.id = GUID.generateUUID();
    subjectSchema.serialNo = subject.serialNo;
    subjectSchema.subjectModelId = subject.subjectModelId;
    subjectSchema.isValid = true;
    subjectSchema.createDate = date;
    subjectSchema.lastUpdateDate = date;
    subjectSchema.isDeleted = false;
    subjectSchema.isOfflineData = true;

    const subjectAttributesSchema = new SubjectAttributesSchema(
      subjectSchema.id,
      1,
      subject.owner,
      subject.country,
      subject.state,
      subject.district,
      subject.site,
      subject.project,
      true
    );
    subjectSchema.subjectAttributes = [subjectAttributesSchema];

    return this.subjectModelDbService.getOne(subject.subjectModelId)
      .pipe(
        mergeMap(x => {
          // Subject Model 存在チェック。
          if (!x) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.DataNotFound} Id: ${subject.subjectModelId}, DataType: ${SubjectModel.name})}`,
              status: HttpStatusCode.NotFound,
              statusText: HttpStatusText.NotFound
            });
          }
          return of(x);
        }),
        mergeMap(() => this.subjectDbService.isSameSerialNumberExists(this.loginUserOfflineService.tenantId, subject.serialNo)),
        mergeMap(x => {
          // 同一シリアル No 存在チェック。 <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Db/Sprocs/AnySubjectHasSameSerialNumber.sql&version=GBdev>
          if (x) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.Conflict.SubjectAlreadyExists}${subject.serialNo}`,
              status: HttpStatusCode.Conflict,
              statusText: HttpStatusText.Conflict
            });
          }
          return of(x);
        }),
        mergeMap(() => this.subjectDbService.putOne(subjectSchema)),
        map(x => this.createSubject(x))
      );
  }

  // SB04 PUT /api/v1/subjects/{subjectId}
  updateSubject(subject: SubjectUpdate): Observable<Subject> {
    return this.subjectModelDbService.getOne(subject.subjectModelId)
      .pipe(
        mergeMap(x => {
          // Subject Model 存在チェック。
          if (!x) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.DataNotFound} Id: ${subject.subjectModelId}, DataType: ${SubjectModel.name})}`,
              status: HttpStatusCode.NotFound,
              statusText: HttpStatusText.NotFound
            });
          }
          return of(x);
        }),
        mergeMap(() => this.subjectDbService.isSameSerialNumberExists(this.loginUserOfflineService.tenantId, subject.serialNo, subject.id)),
        mergeMap(x => {
          // 同一シリアル No 存在チェック。 <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Db/Sprocs/AnySubjectHasSameSerialNumber.sql&version=GBdev>
          if (x) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.Conflict.SubjectAlreadyExists}${subject.serialNo}`,
              status: HttpStatusCode.Conflict,
              statusText: HttpStatusText.Conflict
            });
          }
          return of(x);
        }),
        mergeMap(() => {
          return forkJoin({
            subject: this.subjectDbService.getOne(subject.id),
            hasInspections: this.inspectionDbService.hasInspections(subject.id)
          });
        }),
        mergeMap(value => {
          const originalSubject = value.subject;
          const hasInspections = value.hasInspections;

          // Subject 存在チェック。
          if (!originalSubject) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.DataNotFound} Id: ${originalSubject.id}, DataType: ${Subject.name})}`,
              status: HttpStatusCode.NotFound,
              statusText: HttpStatusText.NotFound
            });
          }

          // 最新Attributesと入力Attributesの差分チェック
          const newestAttributes = originalSubject.subjectAttributes.find(_ => _.isNewestVersion);
          const isAttributesSame =
            newestAttributes.owner === subject.owner &&
            newestAttributes.country === subject.country &&
            newestAttributes.state === subject.state &&
            newestAttributes.district === subject.district &&
            newestAttributes.site === subject.site &&
            newestAttributes.project === subject.project;

          // 既存Subject更新
          originalSubject.serialNo = subject.serialNo;
          originalSubject.subjectModelId = subject.subjectModelId;
          originalSubject.isValid = subject.isValid;
          originalSubject.schemaVersion = subject.schemaVersion;
          originalSubject.lastUpdateDate = new FormatDate().toString();
          originalSubject.lastUpdatedBy = this.loginUserOfflineService.loginUser.id;

          let api$: Observable<SubjectSchema>;

          // バージョンアップ時
          if (!isAttributesSame && hasInspections) {
            newestAttributes.isNewestVersion = false; // update not newest
            const creationAttributes = new SubjectAttributesSchema(
              originalSubject.id,
              newestAttributes.version + 1, // set newest version
              subject.owner,
              subject.country,
              subject.state,
              subject.district,
              subject.site,
              subject.project,
              true // set newest
            );
            api$ = this.subjectDbService.UpdateSubjectAttributesNewestVersion(originalSubject, newestAttributes, creationAttributes);
          }
          // バージョン維持
          else {
            newestAttributes.owner = subject.owner;
            newestAttributes.country = subject.country;
            newestAttributes.state = subject.state;
            newestAttributes.district = subject.district;
            newestAttributes.site = subject.site;
            newestAttributes.project = subject.project;
            originalSubject.subjectAttributes =
              originalSubject.subjectAttributes.map((sa) =>
                sa.isNewestVersion ? newestAttributes : sa
              );
            api$ = this.subjectDbService.putOne(originalSubject);
          }

          return api$;
        }),
        map(x => this.createSubject(x))
      );
  }

  // SB05 DELETE /api/v1/subjects/{subjectId}
  deleteSubject(id: string): Observable<Subject> {
    return this.subjectDbService.getOne(id)
      .pipe(
        mergeMap(schema => {
          // Subject 存在チェック。
          if (!schema) {
            throw new HttpErrorResponse({
              error: `${HttpErrorMessage.DataNotFound} Id: ${id}, DataType: ${Subject.name})}`,
              status: HttpStatusCode.NotFound,
              statusText: HttpStatusText.NotFound
            });
          }

          schema.isDeleted = true;

          schema.lastUpdateDate = new FormatDate().toString();
          schema.lastUpdatedBy = this.loginUserOfflineService.loginUser.id;

          return this.subjectDbService.putOne(schema);
        }),
        map(x => this.createSubject(x))
      );
  }

  private createSubject(schema: SubjectSchema, version?: number, hasInspections?: boolean) {
    const subject = new Subject();
    subject.id = schema.id;
    subject.serialNo = schema.serialNo;
    subject.subjectModelId = schema.subjectModelId;
    subject.isValid = schema.isValid;
    subject.schemaVersion = schema.schemaVersion;
    subject.createdBy = schema.createdBy;
    subject.createDate = schema.createDate;
    subject.lastUpdatedBy = schema.lastUpdatedBy;
    subject.lastUpdateDate = schema.lastUpdateDate;
    subject.isOfflineData = schema.isOfflineData;

    const newestAttributes = schema.subjectAttributes.find(_ =>
      version != null ?
        _.version === version :
        _.isNewestVersion);
    subject.owner = newestAttributes.owner;
    subject.country = newestAttributes.country;
    subject.state = newestAttributes.state;
    subject.district = newestAttributes.district;
    subject.site = newestAttributes.site;
    subject.project = newestAttributes.project;

    subject.hasInspections = hasInspections;
    subject.version = newestAttributes.version;

    return subject;
  }

  // SB06 POST /api/v1/subjects/{subjectId}
  cloneSubject(subject: SubjectClone): Observable<Subject> {
    return of();
  }
}
